import React, { useRef, useState, useEffect } from 'react';
import { Card, Form, Spin, Typography, Button, Col, message, Input, Row, InputNumber } from 'antd';
import { getPhoneNumberValidator, getPhoneEXTValidator } from 'utils';
import PhoneNumberContainer from 'components/PhoneNumberContainer';
import PhoneNumber from 'components/PhoneNumber';
import { useDispatch } from 'react-redux';
import useProviderEditDisabled from 'hooks/useProviderEditDisabled';
import { ADD_AIR_FORCE_HCC_STEPS } from 'constants';
import { PROVIDER_FILE_TYPES } from 'services/providers.service';
import providerService from 'services/providers.service';
import { actions as centerActions } from 'features/add-center';
import { sleep } from 'utils';
import UploadFile from 'components/UploadFile/UploadFile';
import { useAsync } from 'react-async-hook';
import { FormItemLabel } from 'components/Form';
import AriaDatePicker from 'components/AriaDatePicker';
import { validateDates } from 'utils';

const { Title, Paragraph } = Typography;

const AirForceHCCInsurance = ({
  application,
  setApplication,
  id,
  setStep,
  isFormSubmitting,
  loading,
  applicationSubmitted,
}) => {
  const [insuranceFiles, setInsuranceFiles] = useState(null);
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const isSubmitDisabled = useProviderEditDisabled();

  // Keeps track of whether `next` is pressed or `save`
  const shouldGoToNextStep = useRef(false);

  useEffect(() => {
    if (application.liabilityInsurance) {
      form.setFieldsValue({
        liabilityInsurance: application.liabilityInsurance,
      });
    }
  }, [application, form]);

  useAsync(async () => {
    if (id !== undefined) {
      try {
        const _applicationFiles = await providerService.getApplicationFiles(id);
        setInsuranceFiles(_applicationFiles?.filter((file) => file?.fileType === PROVIDER_FILE_TYPES.INSURANCE.code));
      } catch (err) {
        console.error('Error fetching insurance files', err);
      }
    }
  }, [id]);

  return (
    <div id="insurance" data-testid="insurance">
      <Spin spinning={isFormSubmitting || loading}>
        <Form
          layout="vertical"
          form={form}
          disabled={applicationSubmitted}
          scrollToFirstError
          onFinish={async (values) => {
            try {
              dispatch(centerActions.setIsFormSubmitting(true));
              const updatedCenterData = await providerService.updateApplication(id, { ...application, ...values });

              setApplication(updatedCenterData);

              if (shouldGoToNextStep.current) {
                setStep(ADD_AIR_FORCE_HCC_STEPS.AGREEMENT);
              }

              message.success('Saved');
              await sleep(100);
            } catch (err) {
              newrelic.noticeError(err);
              message.error('Unable to save center details');
            } finally {
              dispatch(centerActions.setIsFormSubmitting(false));
            }
          }}
        >
          <Title level={1}>Insurance</Title>
          <Paragraph>
            You must have general liability insurance to be considered for the Air Force Home Community Care program.
          </Paragraph>
          <Card className="mb-4">
            <Col xs={18} sm={16} md={9}>
              <Form.Item hidden name={['liabilityInsurance', 'id']}>
                <Input />
              </Form.Item>

              <Form.Item
                name={['liabilityInsurance', 'issuedBy']}
                label="name of insurance provider"
                rules={[{ required: true, message: 'Name of insurance provider is required' }]}
                data-testid="insuranceProvider"
              >
                <Input />
              </Form.Item>

              <Form.Item
                name={['liabilityInsurance', 'coverageAmount']}
                label={<FormItemLabel title="insured amount" subTitle="Insured amount must be at least $500,000" />}
                rules={[
                  { required: true, message: 'Insured amount is required' },
                  {
                    validator: (_, value) =>
                      value >= 500000 ? Promise.resolve() : Promise.reject('Insured amount must be at least $500,000'),
                  },
                ]}
                validateFirst
                validateTrigger={['onChange', 'onBlur']}
              >
                <InputNumber
                  style={{ borderRadius: '2px', width: '100%' }}
                  min={0}
                  formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, '')}
                  controls={false}
                  changeOnWheel
                  data-testid="insuredAmount"
                />
              </Form.Item>
            </Col>
            <Row>
              <PhoneNumberContainer
                phoneNumber={
                  <Form.Item
                    name={['liabilityInsurance', 'agentPhone']}
                    label="insurance provider phone"
                    rules={[
                      { required: true, message: 'insurance provider phone number is required.' },
                      getPhoneNumberValidator('Invalid Phone Number'),
                    ]}
                  >
                    <PhoneNumber
                      aria-label="insurance provider number"
                      disabled={applicationSubmitted}
                      data-testid="insuranceProviderPhoneNumber"
                    />
                  </Form.Item>
                }
                extension={
                  <Form.Item
                    name={['liabilityInsurance', 'agentPhoneExt']}
                    label="Extension"
                    rules={[getPhoneEXTValidator('Invalid Extension.')]}
                  >
                    <Input
                      placeholder="Extension"
                      aria-label="insurance provider phone number extension"
                      data-testid="insuranceProviderPhoneExt"
                    />
                  </Form.Item>
                }
              />
            </Row>
            <Form.Item
              name={['liabilityInsurance', 'startDate']}
              label="Start date"
              rules={[
                { required: true, message: 'Start date is required.' },
                {
                  validator: validateDates(
                    form.getFieldValue,
                    ['liabilityInsurance', 'startDate'],
                    ['liabilityInsurance', 'endDate'],
                    'Start date must be before end date',
                  ),
                },
              ]}
              className="max-w-96"
            >
              <AriaDatePicker disabled={applicationSubmitted} data-testid="insuranceStartDate" />
            </Form.Item>
            <Form.Item
              name={['liabilityInsurance', 'endDate']}
              label="End date"
              rules={[
                { required: true, message: 'End date is required.' },
                {
                  validator: validateDates(
                    form.getFieldValue,
                    ['liabilityInsurance', 'startDate'],
                    ['liabilityInsurance', 'endDate'],
                    'End date must be after start date',
                  ),
                },
              ]}
              className="max-w-96 mb-8"
            >
              <AriaDatePicker disabled={applicationSubmitted} data-testid={`insuranceEndDate`} />
            </Form.Item>
            <Form.Item
              label={<FormItemLabel title="upload proof of insurance" showAsterisk />}
              name="proofOfInsurance"
              rules={[
                () => ({
                  validator() {
                    if (insuranceFiles.length === 0) {
                      return Promise.reject('File is required');
                    }

                    return Promise.resolve();
                  },
                }),
              ]}
              data-testid="proofOfInsurance"
              className="max-w-96"
            >
              <UploadFile
                fileCode={PROVIDER_FILE_TYPES.INSURANCE.code}
                files={insuranceFiles}
                setFiles={setInsuranceFiles}
                applicationId={id}
                readOnly={applicationSubmitted}
              />
            </Form.Item>
          </Card>
          <div className="flex space-x-2">
            <Button
              onClick={() => setStep(ADD_AIR_FORCE_HCC_STEPS.RESIDENTS)}
              style={{ borderRadius: '0px' }}
              aria-label="back button"
              disabled={false}
              data-testod="backButton"
            >
              Back
            </Button>

            <div className="flex-grow"></div>

            {!applicationSubmitted && (
              <Button
                style={{ borderRadius: '0px' }}
                htmlType="submit"
                onClick={() => {
                  shouldGoToNextStep.current = false;
                }}
                disabled={isFormSubmitting || isSubmitDisabled}
                loading={isFormSubmitting}
                aria-label="save button"
              >
                Save
              </Button>
            )}

            <Button
              style={{ borderRadius: '0px' }}
              type="primary"
              role="button"
              htmlType={!applicationSubmitted ? 'submit' : undefined}
              onClick={() => {
                if (applicationSubmitted) {
                  setStep(ADD_AIR_FORCE_HCC_STEPS.AGREEMENT);
                  return;
                }
                shouldGoToNextStep.current = true;
              }}
              disabled={isFormSubmitting || isSubmitDisabled}
              loading={isFormSubmitting}
              data-testid="insurance-form-submit"
              aria-label="next button"
            >
              Next
            </Button>
          </div>
        </Form>
      </Spin>
    </div>
  );
};

export default AirForceHCCInsurance;
